import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'

import useText from '../../../common/hooks/useText'
import { Table } from '../../../common/uikit/Table'
import { confirm } from '../../../common/uikit/ConfirmDialog'
import { useToast } from '../../../common/uikit/ToastProvider'
import localizeDate from '../../../common/utils/localizeDate'
import {
    type ServicePartnerConnectionResponse,
    type Partner,
    useCompanyGetServicePartnersQuery,
    useCompanyRemoveServicePartnerMutation,
} from '../../../common/redux/api/web-api'
import { WidgetLayout } from '../../../lib/widget-layout'

import { AddServicePartnerDialog } from './dialogs'

export type OrganisationServicePartnersWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    companyId?: string
    canManageServicePartners?: boolean
    partner?: Partner
    isPartnerFetching?: boolean
}

const MAX_SERVICE_PARTNERS = 5

const getServicePartnerAccessStatus = (status?: string | null): 'active' | 'inactive' | 'unknown' => {
    const normalizedStatus = status?.trim().toLowerCase()

    return normalizedStatus === 'active' || normalizedStatus === 'inactive' ? normalizedStatus : 'unknown'
}

export function OrganisationServicePartnersWidget({
    className,
    style,
    companyId,
    canManageServicePartners = false,
    partner,
    isPartnerFetching = false,
}: OrganisationServicePartnersWidgetProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const {
        data: servicePartners = [],
        isFetching: isServicePartnersFetching,
        refetch: refetchServicePartners,
    } = useCompanyGetServicePartnersQuery({ companyId: companyId ?? '' }, { skip: !companyId })
    const [removeServicePartner, { isLoading: isRemoving }] = useCompanyRemoveServicePartnerMutation()
    const [addServicePartnerDialog, setAddServicePartnerDialog] = useState({ visible: false, sessionId: 0 })

    const sortedServicePartners = [...servicePartners].sort((a, b) =>
        (a.partnerName ?? '').localeCompare(b.partnerName ?? '')
    )
    const activeServicePartnerCount = servicePartners.filter(
        (servicePartner) => getServicePartnerAccessStatus(servicePartner.status) === 'active'
    ).length
    const hasReachedServicePartnerLimit = activeServicePartnerCount >= MAX_SERVICE_PARTNERS
    const addServicePartnerDisabled =
        !companyId || isServicePartnersFetching || isPartnerFetching || hasReachedServicePartnerLimit

    const openAddServicePartnerDialog = () => {
        setAddServicePartnerDialog(({ sessionId }) => ({ visible: true, sessionId: sessionId + 1 }))
    }

    const hideAddServicePartnerDialog = () => {
        setAddServicePartnerDialog((dialog) => ({ ...dialog, visible: false }))
    }

    const handleRemove = async (servicePartner: ServicePartnerConnectionResponse) => {
        if (!companyId || !servicePartner.servicePartnerConnectionId) return

        if (getServicePartnerAccessStatus(servicePartner.status) !== 'inactive') {
            const confirmed = await confirm({
                header: text('lblProceedConfirmation'),
                message: text('msgRemoveServicePartnerConfirmation', {
                    partnerName: servicePartner.partnerName || text('lblServicePartnerUnknownPartner'),
                }),
                variant: 'danger',
            })
            if (!confirmed) return
        }

        try {
            await removeServicePartner({
                companyId,
                servicePartnerConnectionId: servicePartner.servicePartnerConnectionId,
            }).unwrap()
            toast.notifySuccess(text('msgServicePartnerRemoved'))
            refetchServicePartners()
        } catch {
            toast.notifyError(text('msgServicePartnerRemoveFailed'))
        }
    }

    const servicePartnerColumns = [
        <Column
            key="partnerName"
            field="partnerName"
            header={text('lblServicePartnerNameColumn')}
            body={(servicePartner: ServicePartnerConnectionResponse) => (
                <span>{servicePartner.partnerName || text('lblServicePartnerUnknownPartner')}</span>
            )}
        />,
        <Column
            key="validTo"
            field="validTo"
            header={text('lblValidTo')}
            body={(servicePartner: ServicePartnerConnectionResponse) =>
                servicePartner.validTo === null
                    ? text('lblServicePartnerIndefiniteValidity')
                    : servicePartner.validTo
                      ? localizeDate(servicePartner.validTo)
                      : '—'
            }
        />,
        <Column
            key="status"
            header={text('lblStatus')}
            body={(servicePartner: ServicePartnerConnectionResponse) => {
                const status = getServicePartnerAccessStatus(servicePartner.status)

                if (status === 'unknown') return '—'

                return (
                    <Tag
                        value={text(status === 'active' ? 'lblActive' : 'lblInactiveStatus')}
                        severity={status === 'active' ? 'success' : 'danger'}
                    />
                )
            }}
        />,
        ...(canManageServicePartners
            ? [
                  <Column
                      key="actions"
                      bodyClassName="text-right"
                      body={(servicePartner: ServicePartnerConnectionResponse) => (
                          <Button
                              tooltip={text('btnRemove')}
                              aria-label={text('btnRemove')}
                              icon="pi pi-times"
                              size="small"
                              severity="danger"
                              outlined
                              rounded
                              disabled={isRemoving}
                              onClick={() => handleRemove(servicePartner)}
                              data-testid={`remove-service-partner-${servicePartner.servicePartnerConnectionId}`}
                          />
                      )}
                  />,
              ]
            : []),
    ]

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblOrganisationServicePartnersWidgetTitle')}
            data-testid="organisation-service-partners-widget"
            headerButtons={
                canManageServicePartners ? (
                    <Button
                        label={text('btnAdd')}
                        icon="pi pi-plus"
                        disabled={addServicePartnerDisabled}
                        tooltip={hasReachedServicePartnerLimit ? text('msgServicePartnerLimitReached') : undefined}
                        tooltipOptions={{ showOnDisabled: true }}
                        onClick={openAddServicePartnerDialog}
                        data-testid="add-service-partner"
                    />
                ) : undefined
            }
        >
            <section>
                <div className="text-sm text-color-secondary mb-2" data-testid="service-partners-count">
                    {text('lblActiveServicePartnerCount', {
                        activeCount: activeServicePartnerCount,
                        limit: MAX_SERVICE_PARTNERS,
                    })}
                </div>
                <Table
                    value={sortedServicePartners}
                    loading={isServicePartnersFetching}
                    dataKey="servicePartnerConnectionId"
                    emptyMessage={text('msgNoServicePartners')}
                    simplified
                    data-testid="service-partners-table"
                >
                    {servicePartnerColumns}
                </Table>
            </section>
            <AddServicePartnerDialog
                key={addServicePartnerDialog.sessionId}
                visible={addServicePartnerDialog.visible}
                companyId={companyId}
                onHide={hideAddServicePartnerDialog}
                onSuccess={refetchServicePartners}
                servicePartners={servicePartners}
                partnerId={partner?.id}
            />
        </WidgetLayout>
    )
}
